import { HeartStraight } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, H6 } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled from 'styled-components';

import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryBidActivityCount,
    getItemSummaryFirstImageUrl,
    getItemSummaryLotNumber,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSavedItemCountNum } from '@/redux/modules/savedItemCount';
import { addImageParams } from '@/utils/urls';

type Props = {
    className?: string;
    itemId: number;
};

const WaitCard = ({ className, itemId }: Props) => {
    const bidActivityCount = useAppSelector((state) => getItemSummaryBidActivityCount(state, itemId));
    const lotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const savedCount = useAppSelector((state) => getSavedItemCountNum(state, itemId));
    const url = useAppSelector((state) => getItemSummaryFirstImageUrl(state, itemId));
    const imageUrl = addImageParams({ height: 232, url, width: 360 });
    const saferTitle = title.length > 44 ? `${title.slice(0, 44).trim()}…` : title;

    return (
        <StyledWaitCard className={className}>
            <StyledCard>
                <StyledH6>
                    <FormattedMessage
                        id="bidding_console.wait.card.title"
                        values={{ lotNumber, title: saferTitle }}
                    />
                </StyledH6>
                <StyledImageContainer>
                    <img
                        alt={title}
                        src={imageUrl}
                    />
                    <StyledGradient />
                    <StyledInfo>
                        {Boolean(savedCount) && (
                            <StyledHeartContainer>
                                {`${savedCount}`}&nbsp;
                                <HeartStraight
                                    size="sm"
                                    style="fill"
                                />
                            </StyledHeartContainer>
                        )}
                        {Boolean(bidActivityCount) && Boolean(savedCount) && <>&nbsp;-&nbsp;</>}
                        {Boolean(bidActivityCount) && (
                            <BodyPrimary>
                                {bidActivityCount}
                                &nbsp;
                                {bidActivityCount === 1 ? (
                                    <FormattedMessage id="bidding_console.wait.card.bid" />
                                ) : (
                                    <FormattedMessage id="bidding_console.wait.card.bids" />
                                )}
                            </BodyPrimary>
                        )}
                    </StyledInfo>
                </StyledImageContainer>
            </StyledCard>
        </StyledWaitCard>
    );
};

export default React.memo<Props>(WaitCard);

const StyledWaitCard = styled.div`
    text-decoration: none;
    width: calc(100% - 30px);
    height: 302px;
    margin: 0 16px 16px;
    position: relative;

    @media (max-width: 374px) {
        height: 257px;
        width: calc(100% - 20px);
        margin: 0 10px 10px;
    }
`;

const StyledCard = styled.div`
    background-color: ${({ theme }) => theme.colors.white};
    position: relative;
    margin: 0 16px 16px;
    padding: 0 16px 16px;
    box-shadow: 0 0 5px ${({ theme }) => theme.colors.shadow200};
    border: 1px solid ${({ theme }) => theme.colors.grey300};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        margin: 0 8px 8px;
        padding: 0 8px 8px;
    }
`;

const StyledH6 = styled(H6)`
    margin: 0;
    padding: 0 15px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    justify-items: center;
    position: relative;
    text-align: center;
`;

const StyledImageContainer = styled.div`
    height: 232px;
    position: relative;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    img {
        max-width: 100%;
        max-height: 100%;
        position: relative;
        z-index: 5;
        display: inline-block;
    }
`;

const StyledGradient = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, transparent 50%, ${({ theme }) => theme.colors.grey400} 100%);
    z-index: 6;
`;

const StyledInfo = styled.div`
    position: absolute;
    bottom: 5px;
    z-index: 10;
    left: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`;

const StyledHeartContainer = styled.span`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`;
